import { AppConfig } from '../config/AppConfig';
import { ApiUtils } from './utils/ApiUtils';
import { AbortStateHandlerClass } from './utils/AbortStateHandler';
import { LoaderStateHandler } from './utils/LoaderStateHandler';
import { RequestConfig, RequestHandler } from './utils/RequestHandler';
import { CacheStateHandler } from './utils/CacheStateHandler';
import { ACCESS_TOKEN, ID_TOKEN, LocalStorage, REFRESH_TOKEN } from '../util/LocalStorage';
import { GlobalApi } from '../util/Global';
import { diContainer } from '../libs/container';
import { AppError, ErrorCodes } from './utils/ErrorHandler';

const cb = () => {};
type func = (error: AppError | string, code?: number) => any;

type ExtendedRequest<T extends { header?: T['header']; body?: T['body']; params?: T['params']; query?: T['query'] }> = {
    header?: T['header'];
    body?: T['body'];
    params?: T['params'];
    query?: T['query'];
};

export interface ApiResponse {
    exec: () => void;
    abort: () => void;
    clearCache: () => void;
}

function delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export interface IServiceHttp<Req extends ExtendedRequest<Req>, Res> {
    headers?: Req['header'];
    params?: Req['params'];
    query?: Req['query'];
    body?: Req['body'];
    skipAuthErrorHandling?: boolean;
    abortController?: AbortController;
    invalidCacheTime?: number;
    retryCount?: number;
    isTokenRefreshed?: boolean;
    onSuccess?: (val: Res) => any;
    onError?: func;
    onFinally?: () => void;
    onBeforeAll?: () => void;
}

export interface IBaseHttp<Req extends ExtendedRequest<Req>> extends IServiceHttp<ExtendedRequest<Req>, any> {
    method: Method;
    url: string;
    isAuthorized?: boolean;
    loaderType?: string;
    fetchFromClient?: boolean;
}

type Method = 'GET' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'POST' | 'PUT' | 'PATCH';

export interface IBaseHttpRequestStatus {
    abort: () => void;
    isRequestInPendingState?: boolean;
}

export class ApiBaseClass {
    constructor(
        private readonly abortStateHandler: AbortStateHandlerClass,
        private readonly cacheStateHandler: CacheStateHandler,
        private readonly loaderStateHandler: LoaderStateHandler,
        private readonly apiUtils: ApiUtils,
        private readonly requestHandler: RequestHandler,
        private readonly localStorage: LocalStorage,
        private readonly globalApi: GlobalApi
    ) {}

    private generateOrPassAbortController(abortController?: AbortController): AbortController {
        if (abortController) return abortController;
        return new AbortController();
    }

    /**
     *
     * @param {Object} exec
     * @string exec.method: GET, POST, DELETE, PUT, PATCH
     * @string exec.url: extension to base url
     * @boolean exec.isAuthorized: will request send authorization credentials
     * @object exec.headers: request headers
     * @object exec.params: string params in request headers
     * @object exec.body: extension to base url
     * @object exec.query: query string params in request
     * @object exec.invalidCacheTime number -
     * How much time it needs to invalidate request data. The default is 1, as 1 ms, which is equivalent to no-cache
     * If the value is 0, it will cache request data for an unlimited period
     * @object exec.retryCount: How many times to retry a request on failure, default is 1. That is two tries in sum.
     * @function exec.onSuccess: success callback function
     * @function exec.onError: error callback function
     * @function exec.onFinally: function that fires after onSuccess or onError
     * @function exec.onBeforeAll: function that fires before onSuccess or onError
     */
    public exec(baseHttpdata: IBaseHttp<any>, isRetry: boolean): void {
        const {
            method = 'GET',
            url,
            fetchFromClient = false,
            isAuthorized = true,
            headers = {},
            params,
            body,
            query,
            abortController,
            loaderType = '',
            invalidCacheTime = 1,
            retryCount = 0,
            skipAuthErrorHandling = false,
            isTokenRefreshed = false,
            onSuccess = cb,
            onError = cb,
            onFinally = cb,
            onBeforeAll = cb,
        } = baseHttpdata;

        if (!isRetry) {
            onBeforeAll();
        }

        const { fullUrl, endpointKey } = this.apiUtils.generateEndpointKey(url, method, params, query);

        const responseData = this.cacheStateHandler.getRequestState(endpointKey);
        if (responseData) {
            onSuccess(responseData);
            onFinally();
            return;
        }

        const isRequestInPendingState = this.cacheStateHandler.isInPendingState(endpointKey);

        if (isRequestInPendingState) {
            if (invalidCacheTime === 1) return;

            this.cacheStateHandler.subscribe(endpointKey, data => {
                if (!data?.isError) return this.exec(baseHttpdata, true);

                if (data.errorName === 'AbortError') return;
                // Create AppError from cached error state
                const appError: AppError = {
                    errorCode: data.errorCode || 'UNKNOWN_ERROR',
                    userMessage: data.userMessage || 'An unexpected error occurred',
                    statusCode: data.statusCode || 500,
                };
                onError(appError, appError.statusCode);
                onFinally();
            });

            return;
        }

        this.cacheStateHandler.setupAsPending(endpointKey);
        this.loaderStateHandler.pushLoaderTypeValue(loaderType, endpointKey);
        const controller = this.generateOrPassAbortController(abortController);

        const config: RequestConfig = {
            method,
            headers,
            body,
        };

        this.abortStateHandler.setRequestState(endpointKey, { abortController: controller });
        config.signal = controller.signal;

        if (isAuthorized) {
            const bearer = `Bearer ${this.localStorage.get(ACCESS_TOKEN)}`;
            config.headers = {
                ...config.headers,
                Authorization: bearer,
                'X-ID-Token': `${this.localStorage.get(ID_TOKEN)}`,
            };
        }

        const baseUrl = fetchFromClient ? '' : AppConfig.serverUrlBase;

        this.requestHandler
            .run(`${baseUrl}/${fullUrl}`, config)
            .then(data => {
                this.cacheStateHandler.setRequestState(endpointKey, data, invalidCacheTime);
                onSuccess(data);
                this.abortStateHandler.deleteRequestState(endpointKey);
                this.loaderStateHandler.popLoaderTypeValue(loaderType, endpointKey);
                onFinally();
            })
            .catch(async error => {
                if (error.name === 'AbortError') {
                    console.log('Request canceled', error);
                    return;
                }
                console.log('Request error', error);
                this.cacheStateHandler.clearPendingState(endpointKey);
                this.abortStateHandler.deleteRequestState(endpointKey);

                if (retryCount > 0 && (error.status === undefined || error.status >= 500)) {
                    await delay(500);
                    return this.exec({ ...baseHttpdata, retryCount: retryCount - 1 }, true);
                }

                let appError: AppError;
                if (error.appError) {
                    appError = error.appError;
                } else {
                    // Fallback for non-FetchError exceptions (legacy/unexpected formats)
                    const rawErrorMessage = error?.message || 'Ooops! Something went wrong';
                    appError = {
                        errorCode: error?.status ? `HTTP_${error.status}` : 'UNKNOWN_ERROR',
                        userMessage: rawErrorMessage,
                        statusCode: error?.status ?? 404,
                    };
                }

                if (!skipAuthErrorHandling && (appError.errorCode === ErrorCodes.UNAUTHORIZED || appError.errorCode === ErrorCodes.HTTP_401)) {
                    const refreshToken = this.localStorage.get(REFRESH_TOKEN);

                    if (!isTokenRefreshed && refreshToken) {
                        return await this.refreshToken(refreshToken, baseHttpdata);
                    } else {
                        return this.signOutUser();
                    }
                }

                onError(appError, appError.statusCode);

                this.loaderStateHandler.popLoaderTypeValue(loaderType, endpointKey);
                onFinally();
                this.cacheStateHandler.notifyListeners(endpointKey, {
                    isError: true,
                    userMessage: appError.userMessage,
                    errorCode: appError.errorCode,
                    statusCode: appError.statusCode,
                });
            });
    }

    private async refreshToken(refreshToken: string, baseHttpdata: IBaseHttp<any>) {
        try {
            const baseUrl = AppConfig.serverUrlBase;
            const url = 'usr/v1/user/refresh-token';
            const config: RequestConfig = {
                method: 'POST',
                headers: new Headers(),
                body: { refreshToken } as any,
            };
            const data = await this.requestHandler.run(`${baseUrl}/${url}`, config);

            this.localStorage.setAuth(data.body);
            return this.exec({ ...baseHttpdata, isTokenRefreshed: true }, true);
        } catch {
            this.signOutUser();
        }
    }

    private signOutUser() {
        this.localStorage.removeAuth();
        this.globalApi.href('/sign-in');
    }

    private abort({ url, params, query, method }: IBaseHttp<any>) {
        const { endpointKey } = this.apiUtils.generateEndpointKey(url, method, params, query);
        const abortState = this.abortStateHandler.getRequestState(endpointKey);
        abortState?.abortController.abort();
        this.abortStateHandler.deleteRequestState(endpointKey);
        this.cacheStateHandler.clearPendingState(endpointKey);
        this.cacheStateHandler.notifyListeners(endpointKey, { isError: true, errorName: 'AbortError' });
    }

    private clearCache({ url, params, query, method }: IBaseHttp<any>) {
        const { endpointKey } = this.apiUtils.generateEndpointKey(url, method, params, query);
        this.cacheStateHandler.clearCache(endpointKey);
    }

    public configure(baseHttpdata: IBaseHttp<ExtendedRequest<any>>): ApiResponse {
        return {
            exec: () => this.exec(baseHttpdata, false),
            abort: () => this.abort(baseHttpdata),
            clearCache: () => this.clearCache(baseHttpdata),
        };
    }
}

diContainer.bindAsDynamic(
    ApiBaseClass,
    c =>
        new ApiBaseClass(
            c.get(AbortStateHandlerClass),
            c.get(CacheStateHandler),
            c.get(LoaderStateHandler),
            c.get(ApiUtils),
            c.get(RequestHandler),
            c.get(LocalStorage),
            c.get(GlobalApi)
        )
);
